import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Helper } from '../../utils/helper/helper';
export class MatDataSource<T> extends MatTableDataSource<T> {
  public dataset: T[];
  public masterSet: T[];
  currentSet: T[];
  infiniteScrollLevel = 0;

  customFilter = (data: T): boolean => true;


  private isJsonString(value: string): boolean {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) || (typeof parsed === "object" && parsed !== null);
    } catch {
      return false;
    }
  }


  public constructor(
    data: T[],
    sort: MatSort,
    disableInfiniteScroll: boolean = false,
    scrollLevel: number = 20
  ) {
    super(
      data.filter((_item, index) =>
        disableInfiniteScroll ? true : index < scrollLevel
      )
    );

    this.masterSet = JSON.parse(JSON.stringify(data));
    this.dataset = [...this.masterSet];

    setTimeout(() => {
      this._filterData([]);
    }, 1000);
    this.dataset.splice(0, scrollLevel);
    this.currentSet = this.data;

    this.infiniteScrollLevel = scrollLevel;
    if (sort) {
      this.sort = sort;
    }


    this._filterData = (data: T[]) => {
      let safeFilter = this.filter;

      // Only escape if it's a plain string, not JSON
      if (typeof this.filter === "string" && !this.isJsonString(this.filter)) {
        safeFilter = Helper.escapeRegex(this.filter);
      }

      this.filteredData = this.masterSet.filter((item) => {
        return (
          this.filterPredicate(item, safeFilter) && this.customFilter(item)
        );
      });

      if (this.filteredData.length === this.masterSet.length) {
        return this.data;
      }

      return this.filteredData.filter(
        (_item, index) => index < this.infiniteScrollLevel
      );
    };



    this.sortData = (_data, sort) => {
      const active = sort.active;
      const direction = sort.direction;

      if (!active || direction === '') {
        return _data;
      }

      return this.filteredData
        .sort((a, b) => {
          let valueA = this.sortingDataAccessor(a, active);
          let valueB = this.sortingDataAccessor(b, active);

          if (active === 'lotNumber') {
            let lotNumberA = typeof valueA === 'number' ? valueA : parseInt(valueA, 10) || 0;
            let lotNumberB = typeof valueB === 'number' ? valueB : parseInt(valueB, 10) || 0;

            if (lotNumberA !== lotNumberB) {
              return (lotNumberA - lotNumberB) * (direction === 'asc' ? 1 : -1);
            }

            let suffixA = String(this.sortingDataAccessor(a, 'lotNumberSuffix') || '');
            let suffixB = String(this.sortingDataAccessor(b, 'lotNumberSuffix') || '');
            return suffixA.localeCompare(suffixB) * (direction === 'asc' ? 1 : -1);
          }

          // Handle date strings (assuming ISO format or other parseable formats)
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            let dateA = Date.parse(valueA);
            let dateB = Date.parse(valueB);
            if (!isNaN(dateA) && !isNaN(dateB)) {
              return (dateA - dateB) * (direction === 'asc' ? 1 : -1);
            }
          }

          let comparatorResult = 0;
          if (valueA != null && valueB != null) {
            comparatorResult = valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
          } else if (valueA != null) {
            comparatorResult = 1;
          } else if (valueB != null) {
            comparatorResult = -1;
          }

          return comparatorResult * (direction === 'asc' ? 1 : -1);
        })
        .filter((_item, index) => index < this.infiniteScrollLevel);
    };
  }

  public searchTable(searchTerm: string): void {
    this.filter = searchTerm;
  }


  public updateDataSet(data: T[]): void {
    this.masterSet = JSON.parse(JSON.stringify(data));

    // Recompute filteredData based on current filter (using _filterData for side effect)
    this._filterData([]);

    // Preserve current scroll level by slicing the new filtered data
    this.dataset = this.filteredData.slice(this.infiniteScrollLevel);

    // Compute the displayed slice, applying sort if active
    let displayed: T[];
    if (this.sort && this.sort.direction !== '') {
      // sortData ignores its data param and uses filteredData internally, then slices to level
      displayed = this.sortData([], this.sort);
    } else {
      displayed = this.filteredData.slice(0, this.infiniteScrollLevel);
    }

    // Update data (emits change for table) and currentSet
    this.data = displayed;
    this.currentSet = this.data;
  }

  public onScroll(): void {
    if (this.dataset.length) {
      this.currentSet.push(...this.dataset.splice(0, 20));
      this.infiniteScrollLevel += 20;
      if (this.sort && this.sort.direction != '') {
        this.data = this.sortData(this.dataset, this.sort);
      } else {
        this.data = this.currentSet;
      }
    }
  }

  updateTable(): void {
    setTimeout(() => {
      this._filterData([]);
    }, 200);
  }

  public loadData(data: T[]): void {
    this.data = data;
  }

  public resetFilters(): void {
    if (this.sort) {
      this.sort.direction = '';
      this.sort.active = undefined;
    }

    this.loadData(this.currentSet);
  }
}
